/**
 * Humanised labels for the agent's tool activity chips. The run channel carries
 * RAW tool names (`generate_image`, `generate_video`, `web_search` — the belt in
 * `apps/backend/src/agent/tool-belt.ts`); rendering those verbatim in the middle
 * of a conversation breaks character in the core product surface — the companion
 * suddenly reads as a function call.
 *
 * A pure name → i18n-key lookup so the mapping is unit-tested with no DOM, and so
 * a new tool is a new ROW here rather than a conditional in the renderer (OCP).
 * An unknown tool falls back to a neutral "she's working on it" line — never the
 * raw identifier, in any locale.
 */

/** i18n key for a tool with no specific line — a companion is simply busy. */
export const GENERIC_TOOL_KEY = 'surface.tools.generic';

/** Tool name → its in-character i18n key. Add a row when the belt gains a tool. */
const TOOL_KEYS: Readonly<Record<string, string>> = {
  generate_image: 'surface.tools.generate_image',
  generate_video: 'surface.tools.generate_video',
  web_search: 'surface.tools.web_search',
};

/**
 * The i18n key describing what the companion is doing, for any tool name. Total:
 * an unrecognised (or empty) name resolves to {@link GENERIC_TOOL_KEY}, so the
 * chip can never leak a raw identifier to the user or a screen reader.
 */
export function toolLabelKey(toolName: string): string {
  return TOOL_KEYS[toolName] ?? GENERIC_TOOL_KEY;
}
